import React, { useState, useEffect } from 'react';
import { 
    Container, Grid, Paper, Typography, Box, CircularProgress, 
    Tabs, Tab, Stack, Card, CardContent, IconButton, Tooltip,
    FormControl, InputLabel, Select, MenuItem, Chip
} from '@mui/material';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    ArcElement,
    Filler
} from 'chart.js';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { 
    TrendingUp, TrendingDown, People, Visibility, 
    Search, TouchApp, InfoOutlined, Refresh
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { 
    useGetUsageStatisticsQuery,
    useGetPerformanceStatisticsQuery, 
    useGetUserAnalyticsQuery,
    useGetAdminAnalyticsQuery,
    useGetAnalyticsSummaryQuery
} from '../rtk/usageStatistics';
import { useAnalytics } from '../hooks/useAnalytics';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    ArcElement,
    Filler
);

const LoadingView = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
    </Box>
);

const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number || 0);
};

const getChangeIcon = (current, previous) => {
    if (current > previous) return <TrendingUp color="success" />;
    if (current < previous) return <TrendingDown color="error" />;
    return null;
};

const getChangePercentage = (current, previous) => {
    if (!previous || previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
};

const MetricCard = ({ title, value, previousValue, icon, color = "primary" }) => {
    const theme = useTheme();
    const changePercent = getChangePercentage(value, previousValue);
    const { t } = useTranslation();
    
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="h6" component="div" color="text.secondary">
                            {title}
                        </Typography>
                        <Typography variant="h4" component="div" color={color}>
                            {formatNumber(value)}
                        </Typography>
                        {previousValue !== undefined && (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                {getChangeIcon(value, previousValue)}
                                <Typography 
                                    variant="body2" 
                                    color={value > previousValue ? "success.main" : value < previousValue ? "error.main" : "text.secondary"}
                                >
                                    {changePercent > 0 ? '+' : ''}{changePercent}% {t('vs yesterday')}
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                    <Box sx={{ color: `${color}.main` }}>
                        {icon}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const PageViewsChart = ({ data, field="page_views_by_day" }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    
    if (!data?.[field]) return <LoadingView />;
    
    const chartData = {
        labels: data[field].map(item => moment(item.date).format('MMM DD')),
        datasets: [
            {
                label: t('Page Views'),
                data: data[field].map(item => item.count),
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main + '20',
                fill: true,
                tension: 0.4,
            }
        ]
    };
    
    return (
        <Box sx={{ height: 300 }}>
            <Line 
                data={chartData} 
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        y: {
                            beginAtZero: true
                        }
                    },
                    plugins: {
                        legend: {
                            display: false
                        }
                    }
                }} 
            />
        </Box>
    );
};

const PopularEndpointsChart = ({ data }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    
    if (!data?.popular_endpoints) return <LoadingView />;
    
    const chartData = {
        labels: data.popular_endpoints.map(item => item.endpoint.replace('/api/', '')),
        datasets: [
            {
                label: t('Requests'),
                data: data.popular_endpoints.map(item => item.count),
                backgroundColor: theme.palette.secondary.main,
                borderColor: theme.palette.secondary.main,
                borderWidth: 1,
                borderRadius: 4,
            }
        ]
    };
    
    return (
        <Box sx={{ height: 300 }}>
            <Bar 
                data={chartData} 
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    indexAxis: 'y',
                    scales: {
                        x: {
                            beginAtZero: true
                        }
                    },
                    plugins: {
                        legend: {
                            display: false
                        }
                    }
                }} 
            />
        </Box>
    );
};

const FeatureUsageChart = ({ data, field="popular_features" }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    
    if (!data?.[field]) return <LoadingView />;
    
    const colors = [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
    ];
    
    const chartData = {
        labels: data[field].map(item => item.feature_name),
        datasets: [
            {
                data: data[field].map(item => item.usage_count),
                backgroundColor: colors,
                borderWidth: 0,
            }
        ]
    };
    
    return (
        <Box sx={{ height: 300 }}>
            <Doughnut 
                data={chartData} 
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom'
                        }
                    }
                }} 
            />
        </Box>
    );
};

const ActiveUsersTable = ({ data }) => {
    const { t } = useTranslation();
    
    if (!data?.active_users) return <LoadingView />;
    
    return (
        <Stack spacing={2}>
            {data.active_users.slice(0, 10).map((user, index) => (
                <Paper key={index} sx={{ p: 2 }} variant="outlined">
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1">
                            {user.user__first_name} {user.user__last_name}
                        </Typography>
                        <Chip 
                            label={t('{{count}} requests', { count: formatNumber(user.request_count) })}
                            color="primary" 
                            size="small" 
                        />
                    </Stack>
                </Paper>
            ))}
        </Stack>
    );
};

const SearchPatternsChart = ({ data, field="popular_searches" }) => {
    const { t } = useTranslation();
    
    if (!data?.[field]) return <LoadingView />;
    
    return (
        <Stack spacing={2} sx={{ maxHeight:"300px", overflow:"auto"}}>
            {data[field].slice(0, 10).map((search, index) => (
                <Paper key={index} sx={{ p: 2 }} variant="outlined">
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1" sx={{ maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            "{search.search_term}"
                        </Typography>
                        <Chip 
                            label={t('{{count}} searches', { count: formatNumber(search.count) })}
                            color="secondary" 
                            size="small" 
                        />
                    </Stack>
                </Paper>
            ))}
        </Stack>
    );
};

const PerformanceChart = ({ data }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    
    if (!data?.performance_by_endpoint) return <LoadingView />;
    
    const chartData = {
        labels: data.performance_by_endpoint.map(item => item.endpoint.replace('/api/', '')),
        datasets: [
            {
                label: t('Avg Response Time (ms)'),
                data: data.performance_by_endpoint.map(item => item.avg_response_time),
                backgroundColor: theme.palette.warning.main,
                borderColor: theme.palette.warning.main,
                borderWidth: 1,
            }
        ]
    };
    
    return (
        <Box sx={{ height: 300 }}>
            <Bar 
                data={chartData} 
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        y: {
                            beginAtZero: true,
                            title: {
                                display: true,
                                text: 'Response Time (ms)'
                            }
                        }
                    },
                    plugins: {
                        legend: {
                            display: false
                        }
                    }
                }} 
            />
        </Box>
    );
};

const UserEngagementWidget = ({ data }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    
    if (!data?.user_engagement) return <LoadingView />;
    
    const getEngagementTooltip = (user) => {
        const breakdown = {
            pageViews: Math.min((user.page_view_count / 1000) * 25, 25),
            apiRequests: Math.min((user.api_request_count / 500) * 15, 15),
            searches: Math.min((user.search_count / 100) * 15, 15),
            features: Math.min((user.feature_usage_count / 50) * 15, 15),
            consistency: Math.min((user.active_days / 30) * 20, 20),
            recency: user.days_since_last_activity <= 1 ? 10 
                   : user.days_since_last_activity <= 7 ? 5 
                   : user.days_since_last_activity <= 30 ? 0 
                   : Math.max(-10, -user.days_since_last_activity / 3)
        };
        
        return (
            <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                    {t('Engagement Score Breakdown')}:
                </Typography>
                <Typography variant="body2">
                    • {t('Page Views')}: {Math.round(breakdown.pageViews)}/25 ({user.page_view_count}/1000)
                </Typography>
                <Typography variant="body2">
                    • {t('API Requests')}: {Math.round(breakdown.apiRequests)}/15 ({user.api_request_count}/500)
                </Typography>
                <Typography variant="body2">
                    • {t('Searches')}: {Math.round(breakdown.searches)}/15 ({user.search_count}/100)
                </Typography>
                <Typography variant="body2">
                    • {t('Features Used')}: {Math.round(breakdown.features)}/15 ({user.feature_usage_count}/50)
                </Typography>
                <Typography variant="body2">
                    • {t('Consistency')}: {Math.round(breakdown.consistency)}/20 ({user.active_days}/30 days)
                </Typography>
                <Typography variant="body2">
                    • {t('Recency')}: {Math.round(breakdown.recency)}/10 ({user.days_since_last_activity} days ago)
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                    {t('Total')}: {user.engagement_score}/100
                </Typography>
            </Stack>
        );
    };
    
    return (
        <Stack spacing={2}>
            {data.user_engagement.slice(0, 15).map((user, index) => (
                <Paper key={index} sx={{ p: 2 }} variant="outlined">
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <Stack>
                                <Typography variant="body1" fontWeight="bold">
                                    {user.first_name} {user.last_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    @{user.username}
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Stack alignItems="center">
                                <Typography variant="h6" color="primary">
                                    {formatNumber(user.page_view_count)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('Page Views')}
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Stack alignItems="center">
                                <Typography variant="h6" color="secondary">
                                    {formatNumber(user.api_request_count)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('API Calls')}
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Stack alignItems="center">
                                <Typography variant="h6" color="success.main">
                                    {formatNumber(user.search_count)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('Searches')}
                                </Typography>
                            </Stack>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Stack alignItems="center">
                                <Typography variant="h6" color="warning.main">
                                    {formatNumber(user.feature_usage_count)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('Features')}
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                    
                    {/* Engagement Score Bar */}
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Tooltip 
                                title={getEngagementTooltip(user)}
                                placement="top-start"
                                sx={{ 
                                    whiteSpace: 'pre-line',
                                    maxWidth: 'none'
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: 'help' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('Engagement Score')}
                                    </Typography>
                                    <InfoOutlined sx={{ fontSize: 12, color: 'text.secondary' }} />
                                </Stack>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary">
                                {user.engagement_score}/100
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                width: '100%',
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: theme.palette.grey[200],
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${Math.min(user.engagement_score, 100)}%`,
                                    height: '100%',
                                    backgroundColor: user.engagement_score >= 80 
                                        ? theme.palette.success.main 
                                        : user.engagement_score >= 50 
                                        ? theme.palette.warning.main 
                                        : theme.palette.error.main,
                                    borderRadius: 4,
                                    transition: 'width 0.5s ease-in-out'
                                }}
                            />
                        </Box>
                    </Box>
                </Paper>
            ))}
        </Stack>
    );
};

const UsageStatisticsView = () => {
    const { t } = useTranslation();
    const { trackFeatureUsage } = useAnalytics();
    const user = useSelector(state => state.user);
    const [tabValue, setTabValue] = useState(0);
    const [dateRange, setDateRange] = useState(30);
    
    // Check if user has admin permissions
    const isAdmin = user?.user?.custom_permissions?.includes("can_read_statistics") || user?.user?.is_admin;
    
    const { data: summary, isLoading: summaryLoading } = useGetAnalyticsSummaryQuery();
    const { data: usageStats, isLoading: usageLoading } = useGetUsageStatisticsQuery({ days: dateRange });
    const { data: performanceStats, isLoading: performanceLoading } = useGetPerformanceStatisticsQuery({ days: dateRange });
    const { data: userAnalytics, isLoading: userAnalyticsLoading } = useGetUserAnalyticsQuery({ days: dateRange });
    const { data: adminAnalytics, isLoading: adminAnalyticsLoading } = useGetAdminAnalyticsQuery({ days: dateRange });
    
    useEffect(() => {
        trackFeatureUsage('usage_statistics_view', {
            tab: tabValue === 0 ? 'overview' : tabValue === 1 ? 'user_analytics' : tabValue === 2 ? 'admin_analytics' : 'performance',
            date_range: dateRange
        });
    }, [tabValue, dateRange, trackFeatureUsage]);
    
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };
    
    if (summaryLoading) return <LoadingView />;
    
    return (
        <Container maxWidth="false" sx={{ mt: 2 }}>
            <Stack spacing={3}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    {t('Usage Statistics')}
                </Typography>
                
                {/* Summary Cards */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title={t('Today\'s Page Views')}
                            value={summary?.today?.page_views}
                            previousValue={summary?.yesterday?.page_views}
                            icon={<Visibility fontSize="large" />}
                            color="primary"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title={t('Active Users Today')}
                            value={summary?.today?.unique_users}
                            previousValue={summary?.yesterday?.unique_users}
                            icon={<People fontSize="large" />}
                            color="secondary"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title={t('API Requests Today')}
                            value={summary?.today?.api_requests}
                            previousValue={summary?.yesterday?.api_requests}
                            icon={<TouchApp fontSize="large" />}
                            color="success"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <MetricCard
                            title={t('Searches Today')}
                            value={summary?.today?.searches}
                            previousValue={summary?.yesterday?.searches}
                            icon={<Search fontSize="large" />}
                            color="warning"
                        />
                    </Grid>
                </Grid>
                
                {/* Tabs for different views */}
                <Paper sx={{ width: '100%' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} centered>
                        <Tab label={t('Overview')} />
                        <Tab label={t('My Analytics')} />
                        {isAdmin && <Tab label={t('Admin Analytics')} />}
                        {isAdmin && <Tab label={t('Performance')} />}
                    </Tabs>
                    
                    <Box sx={{ p: 3 }}>
                        {/* Date Range Selector */}
                        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>{t('Date Range')}</InputLabel>
                                <Select
                                    value={dateRange}
                                    label={t('Date Range')}
                                    onChange={(e) => setDateRange(e.target.value)}
                                >
                                    <MenuItem value={7}> {t('Last 7 days')}</MenuItem>
                                    <MenuItem value={30}>{t('Last 30 days')}</MenuItem>
                                    <MenuItem value={90}>{t('Last 3 months')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        
                        {/* Tab Content */}
                        {tabValue === 0 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} lg={8}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('Page Views')} {dateRange === 30 ? t('Last 30 Days') : t(`Last ${dateRange} Days`)}
                                        </Typography>
                                        {usageLoading ? <LoadingView /> : <PageViewsChart data={usageStats} />}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} lg={4}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('Search Patterns')}
                                        </Typography>
                                        {usageLoading ? <LoadingView /> : <SearchPatternsChart data={usageStats} />}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('Most Used Endpoints')}
                                        </Typography>
                                        {usageLoading ? <LoadingView /> : <PopularEndpointsChart data={usageStats} />}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('Most Active Users')}
                                        </Typography>
                                        {usageLoading ? <LoadingView /> : <ActiveUsersTable data={usageStats} />}
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}
                        
                        {tabValue === 1 && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} lg={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('My Page Views')}
                                        </Typography>
                                        {userAnalyticsLoading ? <LoadingView /> : <PageViewsChart data={userAnalytics} />}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('My Feature Usage')}
                                        </Typography>
                                        {userAnalyticsLoading ? <LoadingView /> : <FeatureUsageChart data={userAnalytics} field={"popular_pages"}/>}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('My Search History')}
                                        </Typography>
                                        {userAnalyticsLoading ? <LoadingView /> : <SearchPatternsChart data={userAnalytics} field={"search_history"}/>}
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}
                        
                        {tabValue === 2 && isAdmin && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} lg={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('Daily Active Users')}
                                        </Typography>
                                        {adminAnalyticsLoading ? <LoadingView /> : <PageViewsChart data={adminAnalytics} field="daily_active_users"/>}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('Feature Popularity')}
                                        </Typography>
                                        {adminAnalyticsLoading ? <LoadingView /> : <FeatureUsageChart data={adminAnalytics} />}
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('User Engagement Dashboard')}
                                        </Typography>
                                        {adminAnalyticsLoading ? <LoadingView /> : <UserEngagementWidget data={adminAnalytics} />}
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}
                        
                        {tabValue === 3 && isAdmin && (
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('API Performance')}
                                        </Typography>
                                        {performanceLoading ? <LoadingView /> : <PerformanceChart data={performanceStats} />}
                                    </Paper>
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                </Paper>
            </Stack>
        </Container>
    );
};

export default UsageStatisticsView;